import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authFetch } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Sparkles,
    Loader2,
    Search,
    Plus,
    Edit,
    Trash2,
    ArrowLeft,
    CheckCircle,
    XCircle,
    AlertCircle,
    MessageSquare,
    ThumbsUp,
    ThumbsDown,
    HelpCircle,
    DollarSign,
    RefreshCw,
    Tag,
    Users,
    Ruler,
} from 'lucide-react';

interface VoiceSample {
    id: string;
    phrase: string;
    sentiment: 'positive' | 'neutral' | 'negative' | 'question';
    source: string;
    confidence_score: number;
}

interface PerceivedAttribute {
    id: string;
    attribute_name: string;
    attribute_type: 'functional' | 'use_case' | 'sustainability' | 'maintenance' | 'trust' | 'value';
    customer_description: string;
    common_phrases: string[];
    technical_features: string[];
    voice_samples?: VoiceSample[];
    research_quality_score?: number;
    created_at: string;
    updated_at: string;
}

interface ResearchSession {
    id: string;
    category_name: string;
    status: string;
    parsed_attributes_count: number;
    error_message?: string;
    created_at: string;
    completed_at?: string;
}

interface AttributeIntelligence {
    id: string;
    title: string;
    scope_attribute_name: string;
    scope_category_path: string;
    research_findings: string;
    knowledge_text: string;
    status: string;
    created_at: string;
    updated_at: string;
}

interface PriceTier {
    max: number | null;
    personas: string[];
}

interface CategoryCharacteristics {
    id: string;
    sectorId: number;
    categoryPath: string;
    categoryName: string;
    status: string;
    aspects: {
        product_types: { data: any[]; hasData: boolean; count: number };
        brand_tiers: { data: Record<string, string[]>; hasData: boolean; tierCount: number };
        use_cases: { data: any[]; hasData: boolean; count: number };
        premium_features: { data: any[]; hasData: boolean; count: number };
        personas: { data: any[]; hasData: boolean; count: number };
        tier_vocabulary: { data: Record<string, { use: string[]; avoid: string[] }>; hasData: boolean; tierCount: number };
        price_tiers: { data: Record<string, PriceTier>; hasData: boolean; tierCount: number };
        spec_semantics: { data: Record<string, any>; hasData: boolean; specCount: number };
        measurement_units: { data: Record<string, any>; hasData: boolean; unitCount: number };
        validation_rules: { data: Record<string, any>; hasData: boolean };
    };
    freshness: { status: string; message: string; daysOld?: number };
    lastResearchedAt: string | null;
    researchCostUsd: number;
}

export default function CategoryResearch() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { selectedSite } = useSite();

    const [categoryName, setCategoryName] = useState(searchParams.get('category') || '');
    const [researching, setResearching] = useState(false);
    const [loading, setLoading] = useState(false);
    const [perceptions, setPerceptions] = useState<PerceivedAttribute[]>([]);
    const [sessions, setSessions] = useState<ResearchSession[]>([]);
    const [availableCategories, setAvailableCategories] = useState<Set<string>>(new Set());
    const [error, setError] = useState<string | null>(null);
    const [selectedPerception, setSelectedPerception] = useState<PerceivedAttribute | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [attributeIntelligence, setAttributeIntelligence] = useState<AttributeIntelligence[]>([]);
    const [researchingIntelligence, setResearchingIntelligence] = useState(false);
    const [characteristics, setCharacteristics] = useState<CategoryCharacteristics | null>(null);
    const [loadingCharacteristics, setLoadingCharacteristics] = useState(false);
    const [refreshingAspect, setRefreshingAspect] = useState<string | null>(null);
    const [resolvedSectorId, setResolvedSectorId] = useState<number | null>(null);
    const [sectorResolved, setSectorResolved] = useState(false);

    // Resolve the correct sector for the current category
    // This ensures research is stored in the same sector AEO will read from
    useEffect(() => {
        async function resolveSector() {
            // If site has multiple sectors and we have a category, resolve the correct one
            if (selectedSite?.sector_ids && selectedSite.sector_ids.length > 1 && categoryName) {
                try {
                    const sectorIds = selectedSite.sector_ids.join(',');
                    const response = await authFetch(
                        `/category-characteristics/resolve-sector?sectorIds=${sectorIds}&categoryName=${encodeURIComponent(categoryName)}`
                    );
                    const data = await response.json();

                    if (data.success) {
                        setResolvedSectorId(data.sectorId);
                        setSectorResolved(data.resolved);
                        console.log(`[CategoryResearch] Resolved sector: ${data.sectorId} (resolved=${data.resolved}) for "${categoryName}"`);
                    } else {
                        // Fallback to primary sector
                        setResolvedSectorId(selectedSite.sector_id || null);
                        setSectorResolved(false);
                    }
                } catch (error) {
                    console.error('Failed to resolve sector:', error);
                    setResolvedSectorId(selectedSite.sector_id || null);
                    setSectorResolved(false);
                }
            } else {
                // Single sector or no category - use primary sector
                setResolvedSectorId(selectedSite?.sector_id || null);
                setSectorResolved(false);
            }
        }

        resolveSector();
    }, [selectedSite?.sector_ids, selectedSite?.sector_id, categoryName]);

    // Load research sessions and available categories when site changes
    useEffect(() => {
        if (selectedSite?.sector_id) {
            loadSessions();
            // Clear perceptions when switching sites
            setPerceptions([]);
        }
    }, [selectedSite?.sector_id]);

    // Load perceptions, attribute intelligence, and characteristics when resolved sector or category changes
    useEffect(() => {
        // Wait for sector resolution before loading
        if (resolvedSectorId && categoryName) {
            loadPerceptions();
            loadAttributeIntelligence();
            loadCharacteristics();
        } else if (!categoryName) {
            // Clear data if category is cleared
            setPerceptions([]);
            setAttributeIntelligence([]);
            setCharacteristics(null);
        }
    }, [categoryName, resolvedSectorId]);

    async function loadSessions() {
        try {
            const response = await authFetch(`/category-research/${selectedSite.sector_id}/sessions`);
            const data = await response.json();
            if (data.success) {
                setSessions(data.sessions);
                // Extract unique category names from completed sessions
                const categories = new Set<string>();
                data.sessions.forEach((session: ResearchSession) => {
                    if (session.status === 'completed' && session.category_name) {
                        categories.add(session.category_name);
                    }
                });
                setAvailableCategories(categories);
            }
        } catch (err: any) {
            console.error('Error loading sessions:', err);
        }
    }

    async function loadPerceptions() {
        // Use resolved sector for category-level data
        const sectorId = resolvedSectorId || selectedSite.sector_id;
        if (!sectorId) return;

        try {
            setLoading(true);
            setError(null);
            const response = await authFetch(
                `/category-research/${sectorId}/perceptions/${encodeURIComponent(categoryName)}`
            );
            const data = await response.json();

            if (data.success) {
                setPerceptions(data.perceptions);
            } else {
                setError(data.error || 'Failed to load perceptions');
            }
        } catch (err: any) {
            console.error('Error loading perceptions:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    async function loadAttributeIntelligence() {
        // Use resolved sector for category-level data
        const sectorId = resolvedSectorId || selectedSite.sector_id;
        if (!sectorId) return;

        try {
            const siteId = selectedSite.id || selectedSite._id;
            const response = await authFetch(
                `/category-research/${sectorId}/attribute-intelligence/${siteId}?categoryName=${encodeURIComponent(categoryName)}`
            );
            const data = await response.json();

            if (data.success) {
                setAttributeIntelligence(data.intelligence);
            }
        } catch (err: any) {
            console.error('Error loading attribute intelligence:', err);
        }
    }

    async function loadCharacteristics() {
        if (!categoryName) return;

        // Use resolved sector for category-level data
        const sectorId = resolvedSectorId || selectedSite.sector_id;
        if (!sectorId) return;

        try {
            setLoadingCharacteristics(true);
            // Find the full category path from available categories or use the name directly
            const categoryPath = categoryName;
            const response = await authFetch(
                `/category-characteristics/${sectorId}/category/${encodeURIComponent(categoryPath)}`
            );
            const data = await response.json();

            if (data.success && data.data) {
                setCharacteristics(data.data);
            } else {
                setCharacteristics(null);
            }
        } catch (err: any) {
            console.error('Error loading characteristics:', err);
            setCharacteristics(null);
        } finally {
            setLoadingCharacteristics(false);
        }
    }

    async function refreshCharacteristicsAspect(aspect: string) {
        if (!categoryName) return;

        // Use resolved sector for category-level data
        const sectorId = resolvedSectorId || selectedSite.sector_id;
        if (!sectorId) return;

        try {
            setRefreshingAspect(aspect);
            const categoryPath = categoryName;
            const response = await authFetch(
                `/category-characteristics/${sectorId}/category/${encodeURIComponent(categoryPath)}/refresh/${aspect}`,
                { method: 'POST' }
            );
            const data = await response.json();

            if (data.success) {
                await loadCharacteristics();
            } else {
                alert(`Failed to refresh ${aspect}: ${data.error}`);
            }
        } catch (err: any) {
            console.error(`Error refreshing ${aspect}:`, err);
            alert(`Failed to refresh ${aspect}: ${err.message}`);
        } finally {
            setRefreshingAspect(null);
        }
    }

    async function refreshAllCharacteristics() {
        if (!categoryName) return;

        // Use resolved sector for category-level data
        const sectorId = resolvedSectorId || selectedSite.sector_id;
        if (!sectorId) return;

        try {
            setLoadingCharacteristics(true);
            const categoryPath = categoryName;
            const response = await authFetch(
                `/category-characteristics/${sectorId}/category/${encodeURIComponent(categoryPath)}/refresh`,
                { method: 'POST' }
            );
            const data = await response.json();

            if (data.success) {
                await loadCharacteristics();
                alert('Category characteristics refreshed successfully!');
            } else {
                alert(`Failed to refresh: ${data.error}`);
            }
        } catch (err: any) {
            console.error('Error refreshing characteristics:', err);
            alert(`Failed to refresh: ${err.message}`);
        } finally {
            setLoadingCharacteristics(false);
        }
    }

    async function handleResearchIntelligence() {
        if (!categoryName.trim()) {
            alert('Please enter a category name');
            return;
        }

        // Use resolved sector for category-level data
        const sectorId = resolvedSectorId || selectedSite.sector_id;
        if (!sectorId) {
            alert('No sector resolved for this category');
            return;
        }

        try {
            setResearchingIntelligence(true);
            const siteId = selectedSite.id || selectedSite._id;

            const response = await authFetch(`/category-research/${sectorId}/attribute-intelligence`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    categoryName: categoryName.trim(),
                    siteId
                })
            });

            const data = await response.json();

            if (data.success) {
                alert(`✓ Attribute Intelligence research completed!\n\n${data.attributesResearched} attribute interpretations generated.`);
                await loadAttributeIntelligence();
            } else {
                throw new Error(data.error || 'Research failed');
            }
        } catch (err: any) {
            console.error('Error researching attribute intelligence:', err);
            alert(`Research failed: ${err.message}`);
        } finally {
            setResearchingIntelligence(false);
        }
    }

    async function handleResearch() {
        if (!categoryName.trim()) {
            alert('Please enter a category name');
            return;
        }

        // Use resolved sector for category-level data
        const sectorId = resolvedSectorId || selectedSite.sector_id;
        if (!sectorId) {
            alert('No sector resolved for this category');
            return;
        }

        try {
            setResearching(true);
            setError(null);

            const response = await authFetch(`/category-research/${sectorId}/research`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ categoryName: categoryName.trim() })
            });

            const data = await response.json();

            if (data.success) {
                alert(`✓ Research completed!\n\n${data.attributeCount} perceived attributes identified.`);
                await loadPerceptions();
                await loadSessions();
            } else {
                throw new Error(data.error || 'Research failed');
            }
        } catch (err: any) {
            console.error('Error researching category:', err);
            setError(err.message);
            alert(`Research failed: ${err.message}`);
        } finally {
            setResearching(false);
        }
    }

    async function handleDelete(perceptionId: string) {
        if (!confirm('Are you sure you want to delete this perceived attribute?')) {
            return;
        }

        // Use resolved sector for category-level data
        const sectorId = resolvedSectorId || selectedSite.sector_id;
        if (!sectorId) return;

        try {
            await authFetch(`/category-research/${sectorId}/perceptions/${perceptionId}`, {
                method: 'DELETE'
            });
            await loadPerceptions();
        } catch (err: any) {
            console.error('Error deleting perception:', err);
            alert(`Delete failed: ${err.message}`);
        }
    }

    function openEditDialog(perception: PerceivedAttribute) {
        setSelectedPerception(perception);
        setEditDialogOpen(true);
    }

    async function handleSaveEdit() {
        if (!selectedPerception) return;

        // Use resolved sector for category-level data
        const sectorId = resolvedSectorId || selectedSite.sector_id;
        if (!sectorId) return;

        try {
            await authFetch(`/category-research/${sectorId}/perceptions/${selectedPerception.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    attribute_name: selectedPerception.attribute_name,
                    customer_description: selectedPerception.customer_description,
                    common_phrases: selectedPerception.common_phrases,
                    technical_features: selectedPerception.technical_features
                })
            });

            setEditDialogOpen(false);
            setSelectedPerception(null);
            await loadPerceptions();
        } catch (err: any) {
            console.error('Error updating perception:', err);
            alert(`Update failed: ${err.message}`);
        }
    }

    function getStatusBadge(status: string) {
        switch (status) {
            case 'completed':
                return <Badge variant="default" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Completed</Badge>;
            case 'failed':
                return <Badge variant="destructive" className="flex items-center gap-1"><XCircle className="w-3 h-3" /> Failed</Badge>;
            case 'in_progress':
                return <Badge variant="secondary" className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> In Progress</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    }

    function getAttributeTypeBadge(type: string) {
        const colors: Record<string, string> = {
            functional: 'bg-blue-100 text-blue-800 border-blue-200',
            use_case: 'bg-green-100 text-green-800 border-green-200',
            sustainability: 'bg-emerald-100 text-emerald-800 border-emerald-200',
            maintenance: 'bg-orange-100 text-orange-800 border-orange-200',
            trust: 'bg-purple-100 text-purple-800 border-purple-200',
            value: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        };
        const labels: Record<string, string> = {
            functional: 'Functional',
            use_case: 'Use Case',
            sustainability: 'Sustainability',
            maintenance: 'Maintenance',
            trust: 'Trust',
            value: 'Value',
        };
        return (
            <Badge className={`${colors[type] || 'bg-gray-100 text-gray-800'} border`}>
                {labels[type] || type}
            </Badge>
        );
    }

    function getSentimentIcon(sentiment: string) {
        switch (sentiment) {
            case 'positive':
                return <ThumbsUp className="w-3 h-3 text-green-600" />;
            case 'negative':
                return <ThumbsDown className="w-3 h-3 text-red-600" />;
            case 'question':
                return <HelpCircle className="w-3 h-3 text-blue-600" />;
            default:
                return <MessageSquare className="w-3 h-3 text-gray-600" />;
        }
    }

    function getSentimentBadgeClass(sentiment: string) {
        switch (sentiment) {
            case 'positive':
                return 'bg-green-50 text-green-700 border-green-200';
            case 'negative':
                return 'bg-red-50 text-red-700 border-red-200';
            case 'question':
                return 'bg-blue-50 text-blue-700 border-blue-200';
            default:
                return 'bg-gray-50 text-gray-700 border-gray-200';
        }
    }

    if (!selectedSite) {
        return (
            <div className="p-8">
                <div className="flex items-center gap-2 text-yellow-600">
                    <AlertCircle className="w-5 h-5" />
                    <p>Please select a site first</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-8">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                    <Button onClick={() => navigate(-1)} variant="ghost" size="sm">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </Button>
                </div>
                <h1 className="text-3xl font-bold">Category Perception Research</h1>
                <p className="text-gray-600">
                    Research customer-perceived attributes that bridge technical specs and customer needs
                </p>
            </div>

            {/* Research Form */}
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>Research a Product Category</CardTitle>
                    <CardDescription>
                        Identify the perceived attributes customers care about when choosing products in this category
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {/* Previously Researched Categories */}
                        {availableCategories.size > 0 && (
                            <div>
                                <Label className="text-xs font-semibold text-gray-600">Previously Researched Categories</Label>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {Array.from(availableCategories).map((cat) => (
                                        <Badge
                                            key={cat}
                                            variant={categoryName === cat ? "default" : "outline"}
                                            className="cursor-pointer hover:bg-primary hover:text-white transition-colors"
                                            onClick={() => setCategoryName(cat)}
                                        >
                                            {cat}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Research Input */}
                        <div className="flex gap-4">
                            <div className="flex-1">
                                <Label htmlFor="category">Product Category</Label>
                                <Input
                                    id="category"
                                    placeholder="e.g., Bike wheels and tires"
                                    value={categoryName}
                                    onChange={(e) => setCategoryName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleResearch();
                                        }
                                    }}
                                />
                            </div>
                            <div className="flex items-end">
                                <Button onClick={handleResearch} disabled={researching || !categoryName.trim()}>
                                    {researching ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Researching...
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="w-4 h-4 mr-2" />
                                            Research Category
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>

                        {error && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                                {error}
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs defaultValue="perceptions" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="perceptions">
                        Perceived Benefits ({perceptions.length})
                    </TabsTrigger>
                    <TabsTrigger value="intelligence">
                        Attribute Intelligence ({attributeIntelligence.length})
                    </TabsTrigger>
                    <TabsTrigger value="characteristics">
                        Category Characteristics {characteristics?.aspects?.price_tiers?.hasData ? '✓' : ''}
                    </TabsTrigger>
                    <TabsTrigger value="sessions">
                        Research Sessions ({sessions.length})
                    </TabsTrigger>
                </TabsList>

                {/* Perceived Attributes Tab */}
                <TabsContent value="perceptions">
                    <Card>
                        <CardHeader>
                            <CardTitle>Perceived Attributes for "{categoryName || 'Category'}"</CardTitle>
                            <CardDescription>
                                Customer-facing attributes that matter when choosing products
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                                </div>
                            ) : perceptions.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <Search className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                    <p>No perceived attributes found for this category</p>
                                    <p className="text-sm">Research a category to generate insights</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {perceptions.map((perception) => (
                                        <Card key={perception.id} className="border border-gray-200">
                                            <CardHeader>
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <CardTitle className="text-lg">{perception.attribute_name}</CardTitle>
                                                            {getAttributeTypeBadge(perception.attribute_type || 'functional')}
                                                            {perception.research_quality_score && (
                                                                <Badge variant="outline" className="text-xs">
                                                                    Quality: {(perception.research_quality_score * 100).toFixed(0)}%
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <CardDescription className="mt-2">
                                                            {perception.customer_description}
                                                        </CardDescription>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => openEditDialog(perception)}
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDelete(perception.id)}
                                                        >
                                                            <Trash2 className="w-4 h-4 text-red-600" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="space-y-4">
                                                    {/* Customer Voice Samples */}
                                                    {perception.voice_samples && perception.voice_samples.length > 0 && (
                                                        <div>
                                                            <Label className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                                                                <MessageSquare className="w-3 h-3" />
                                                                Customer Voice Samples
                                                            </Label>
                                                            <div className="space-y-2 mt-2">
                                                                {perception.voice_samples.slice(0, 5).map((sample, i) => (
                                                                    <div
                                                                        key={i}
                                                                        className={`flex items-start gap-2 p-2 rounded border ${getSentimentBadgeClass(sample.sentiment)}`}
                                                                    >
                                                                        {getSentimentIcon(sample.sentiment)}
                                                                        <span className="text-sm flex-1">"{sample.phrase}"</span>
                                                                        {sample.confidence_score && (
                                                                            <span className="text-xs opacity-70">
                                                                                {(sample.confidence_score * 100).toFixed(0)}%
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                                {perception.voice_samples.length > 5 && (
                                                                    <div className="text-xs text-gray-500 text-center">
                                                                        +{perception.voice_samples.length - 5} more samples
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Common Customer Phrases */}
                                                    <div>
                                                        <Label className="text-xs font-semibold text-gray-600">Common Customer Phrases</Label>
                                                        <div className="flex flex-wrap gap-2 mt-2">
                                                            {perception.common_phrases?.map((phrase, i) => (
                                                                <Badge key={i} variant="secondary">{phrase}</Badge>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Related Technical Features */}
                                                    <div>
                                                        <Label className="text-xs font-semibold text-gray-600">Related Technical Features</Label>
                                                        <div className="flex flex-wrap gap-2 mt-2">
                                                            {perception.technical_features?.map((feature, i) => (
                                                                <Badge key={i} variant="outline">{feature}</Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Attribute Intelligence Tab */}
                <TabsContent value="intelligence">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Attribute Intelligence for "{categoryName || 'Category'}"</CardTitle>
                                    <CardDescription>
                                        Explains what technical spec values mean to customers (e.g., TPI=60 vs TPI=120)
                                    </CardDescription>
                                </div>
                                <Button
                                    onClick={handleResearchIntelligence}
                                    disabled={researchingIntelligence || !categoryName.trim() || perceptions.length === 0}
                                    variant="outline"
                                >
                                    {researchingIntelligence ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Researching...
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="w-4 h-4 mr-2" />
                                            Research Attribute Values
                                        </>
                                    )}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {perceptions.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
                                    <p className="font-medium">Research Perceived Benefits First</p>
                                    <p className="text-sm mt-1">
                                        Attribute intelligence is derived from technical features in perceived benefits.
                                        Research the category first to identify technical features.
                                    </p>
                                </div>
                            ) : attributeIntelligence.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <Search className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                    <p>No attribute intelligence found for this category</p>
                                    <p className="text-sm">Click "Research Attribute Values" to generate interpretations</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {attributeIntelligence.map((intel) => (
                                        <Card key={intel.id} className="border border-gray-200">
                                            <CardHeader>
                                                <div className="flex items-center gap-2">
                                                    <CardTitle className="text-lg">{intel.title}</CardTitle>
                                                    <Badge variant="outline">{intel.scope_attribute_name}</Badge>
                                                </div>
                                                <CardDescription>{intel.knowledge_text}</CardDescription>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="bg-gray-50 p-4 rounded-lg">
                                                    <Label className="text-xs font-semibold text-gray-600">Value Interpretations</Label>
                                                    <pre className="mt-2 text-sm whitespace-pre-wrap font-mono text-gray-700">
                                                        {intel.research_findings}
                                                    </pre>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Category Characteristics Tab */}
                <TabsContent value="characteristics">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Category Characteristics for "{categoryName || 'Category'}"</CardTitle>
                                    <CardDescription>
                                        AI-researched market data including price tiers, brand tiers, and spec semantics
                                    </CardDescription>
                                </div>
                                <Button
                                    onClick={refreshAllCharacteristics}
                                    disabled={loadingCharacteristics || !categoryName.trim()}
                                    variant="outline"
                                >
                                    {loadingCharacteristics ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Researching...
                                        </>
                                    ) : (
                                        <>
                                            <RefreshCw className="w-4 h-4 mr-2" />
                                            Refresh All
                                        </>
                                    )}
                                </Button>
                            </div>
                            {characteristics?.freshness && (
                                <div className="mt-2">
                                    <Badge variant={
                                        characteristics.freshness.status === 'fresh' ? 'default' :
                                        characteristics.freshness.status === 'stale' ? 'destructive' : 'secondary'
                                    }>
                                        {characteristics.freshness.message}
                                    </Badge>
                                    {characteristics.researchCostUsd > 0 && (
                                        <span className="ml-2 text-xs text-muted-foreground">
                                            Research cost: ${characteristics.researchCostUsd.toFixed(4)}
                                        </span>
                                    )}
                                </div>
                            )}
                        </CardHeader>
                        <CardContent>
                            {loadingCharacteristics ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                                </div>
                            ) : !characteristics ? (
                                <div className="text-center py-8 text-gray-500">
                                    <Search className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                    <p>No characteristics found for this category</p>
                                    <p className="text-sm">Click "Refresh All" to research category characteristics</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Price Tiers Section */}
                                    <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <DollarSign className="w-5 h-5 text-green-600" />
                                                <h3 className="font-semibold">Price Tiers</h3>
                                                {characteristics.aspects.price_tiers?.hasData ? (
                                                    <Badge variant="default" className="text-xs">Researched</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-xs">Not researched</Badge>
                                                )}
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => refreshCharacteristicsAspect('price_tiers')}
                                                disabled={refreshingAspect === 'price_tiers'}
                                            >
                                                {refreshingAspect === 'price_tiers' ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="w-4 h-4" />
                                                )}
                                            </Button>
                                        </div>
                                        {characteristics.aspects.price_tiers?.hasData ? (
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                {['entry', 'mid_range', 'premium', 'ultra_premium'].map((tier) => {
                                                    const tierData = characteristics.aspects.price_tiers.data[tier];
                                                    if (!tierData) return null;
                                                    return (
                                                        <div key={tier} className="bg-muted/50 rounded-lg p-3">
                                                            <div className="font-medium text-sm capitalize mb-1">
                                                                {tier.replace('_', ' ')}
                                                            </div>
                                                            <div className="text-lg font-bold text-primary">
                                                                {tierData.max ? `≤ €${tierData.max}` : 'No limit'}
                                                            </div>
                                                            {tierData.personas?.length > 0 && (
                                                                <div className="mt-2 flex flex-wrap gap-1">
                                                                    {tierData.personas.slice(0, 3).map((persona: string, i: number) => (
                                                                        <Badge key={i} variant="outline" className="text-xs">
                                                                            {persona.replace('_', ' ')}
                                                                        </Badge>
                                                                    ))}
                                                                    {tierData.personas.length > 3 && (
                                                                        <Badge variant="outline" className="text-xs">
                                                                            +{tierData.personas.length - 3}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-muted-foreground">
                                                No price tiers researched yet. Click refresh to research market-based price tiers.
                                            </p>
                                        )}
                                    </div>

                                    {/* Brand Tiers Section */}
                                    <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <Tag className="w-5 h-5 text-blue-600" />
                                                <h3 className="font-semibold">Brand Tiers</h3>
                                                {characteristics.aspects.brand_tiers?.hasData ? (
                                                    <Badge variant="default" className="text-xs">Researched</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-xs">Not researched</Badge>
                                                )}
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => refreshCharacteristicsAspect('brand_tiers')}
                                                disabled={refreshingAspect === 'brand_tiers'}
                                            >
                                                {refreshingAspect === 'brand_tiers' ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="w-4 h-4" />
                                                )}
                                            </Button>
                                        </div>
                                        {characteristics.aspects.brand_tiers?.hasData ? (
                                            <div className="grid grid-cols-3 gap-4">
                                                {['premium', 'mid', 'entry'].map((tier) => {
                                                    const brands = characteristics.aspects.brand_tiers.data[tier] || [];
                                                    return (
                                                        <div key={tier}>
                                                            <div className="font-medium text-sm capitalize mb-2">{tier}</div>
                                                            <div className="flex flex-wrap gap-1">
                                                                {brands.slice(0, 8).map((brand: string, i: number) => (
                                                                    <Badge key={i} variant="secondary" className="text-xs">
                                                                        {brand}
                                                                    </Badge>
                                                                ))}
                                                                {brands.length > 8 && (
                                                                    <Badge variant="outline" className="text-xs">
                                                                        +{brands.length - 8}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-muted-foreground">
                                                No brand tiers researched yet.
                                            </p>
                                        )}
                                    </div>

                                    {/* Personas Section */}
                                    <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <Users className="w-5 h-5 text-purple-600" />
                                                <h3 className="font-semibold">Buyer Personas</h3>
                                                {characteristics.aspects.personas?.hasData ? (
                                                    <Badge variant="default" className="text-xs">
                                                        {characteristics.aspects.personas.count} personas
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-xs">Not researched</Badge>
                                                )}
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => refreshCharacteristicsAspect('personas')}
                                                disabled={refreshingAspect === 'personas'}
                                            >
                                                {refreshingAspect === 'personas' ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="w-4 h-4" />
                                                )}
                                            </Button>
                                        </div>
                                        {characteristics.aspects.personas?.hasData ? (
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                                {characteristics.aspects.personas.data.map((persona: any, i: number) => (
                                                    <div key={i} className="bg-muted/50 rounded-lg p-3">
                                                        <div className="font-medium text-sm">
                                                            {persona.name?.en || persona.persona_key}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground mt-1">
                                                            {persona.description?.en?.substring(0, 80)}...
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-muted-foreground">
                                                No personas researched yet.
                                            </p>
                                        )}
                                    </div>

                                    {/* Spec Semantics Section */}
                                    <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <Ruler className="w-5 h-5 text-orange-600" />
                                                <h3 className="font-semibold">Spec Semantics</h3>
                                                {characteristics.aspects.spec_semantics?.hasData ? (
                                                    <Badge variant="default" className="text-xs">
                                                        {characteristics.aspects.spec_semantics.specCount} specs
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-xs">Not researched</Badge>
                                                )}
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => refreshCharacteristicsAspect('spec_semantics')}
                                                disabled={refreshingAspect === 'spec_semantics'}
                                            >
                                                {refreshingAspect === 'spec_semantics' ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="w-4 h-4" />
                                                )}
                                            </Button>
                                        </div>
                                        {characteristics.aspects.spec_semantics?.hasData ? (
                                            <div className="space-y-3">
                                                {Object.entries(characteristics.aspects.spec_semantics.data).slice(0, 5).map(([spec, data]: [string, any]) => (
                                                    <div key={spec} className="bg-muted/50 rounded-lg p-3">
                                                        <div className="font-medium text-sm">{data.display_name || spec}</div>
                                                        <div className="text-xs text-muted-foreground mt-1">{data.description}</div>
                                                        {data.ranges && (
                                                            <div className="mt-2 flex flex-wrap gap-1">
                                                                {data.ranges.map((range: any, i: number) => (
                                                                    <Badge key={i} variant="outline" className="text-xs">
                                                                        {range.range}: {range.label}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-muted-foreground">
                                                No spec semantics researched yet. This helps explain what spec values mean (e.g., TPI 120 = premium).
                                            </p>
                                        )}
                                    </div>

                                    {/* Tier Vocabulary Section */}
                                    <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <MessageSquare className="w-5 h-5 text-teal-600" />
                                                <h3 className="font-semibold">Tier Vocabulary</h3>
                                                {characteristics.aspects.tier_vocabulary?.hasData ? (
                                                    <Badge variant="default" className="text-xs">Researched</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-xs">Not researched</Badge>
                                                )}
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => refreshCharacteristicsAspect('tier_vocabulary')}
                                                disabled={refreshingAspect === 'tier_vocabulary'}
                                            >
                                                {refreshingAspect === 'tier_vocabulary' ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="w-4 h-4" />
                                                )}
                                            </Button>
                                        </div>
                                        {characteristics.aspects.tier_vocabulary?.hasData ? (
                                            <div className="grid grid-cols-2 gap-4">
                                                {['entry', 'mid_range', 'premium', 'ultra_premium'].map((tier) => {
                                                    const vocab = characteristics.aspects.tier_vocabulary.data[tier];
                                                    if (!vocab) return null;
                                                    return (
                                                        <div key={tier} className="bg-muted/50 rounded-lg p-3">
                                                            <div className="font-medium text-sm capitalize mb-2">
                                                                {tier.replace('_', ' ')}
                                                            </div>
                                                            <div className="space-y-2">
                                                                <div>
                                                                    <span className="text-xs text-green-600 font-medium">Use:</span>
                                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                                        {vocab.use?.slice(0, 4).map((term: string, i: number) => (
                                                                            <Badge key={i} variant="secondary" className="text-xs bg-green-50">
                                                                                {term}
                                                                            </Badge>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <span className="text-xs text-red-600 font-medium">Avoid:</span>
                                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                                        {vocab.avoid?.slice(0, 4).map((term: string, i: number) => (
                                                                            <Badge key={i} variant="secondary" className="text-xs bg-red-50">
                                                                                {term}
                                                                            </Badge>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-muted-foreground">
                                                No tier vocabulary researched yet. This guides what language to use/avoid per price tier.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Research Sessions Tab */}
                <TabsContent value="sessions">
                    <Card>
                        <CardHeader>
                            <CardTitle>Research Sessions</CardTitle>
                            <CardDescription>History of category perception research</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Category</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Attributes Found</TableHead>
                                        <TableHead>Created</TableHead>
                                        <TableHead>Completed</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sessions.map((session) => (
                                        <TableRow key={session.id}>
                                            <TableCell className="font-medium">{session.category_name}</TableCell>
                                            <TableCell>{getStatusBadge(session.status)}</TableCell>
                                            <TableCell>{session.parsed_attributes_count}</TableCell>
                                            <TableCell>{new Date(session.created_at).toLocaleString()}</TableCell>
                                            <TableCell>
                                                {session.completed_at
                                                    ? new Date(session.completed_at).toLocaleString()
                                                    : '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Edit Perceived Attribute</DialogTitle>
                        <DialogDescription>Update the attribute details</DialogDescription>
                    </DialogHeader>
                    {selectedPerception && (
                        <div className="space-y-4">
                            <div>
                                <Label>Attribute Name</Label>
                                <Input
                                    value={selectedPerception.attribute_name}
                                    onChange={(e) =>
                                        setSelectedPerception({
                                            ...selectedPerception,
                                            attribute_name: e.target.value
                                        })
                                    }
                                />
                            </div>
                            <div>
                                <Label>Customer Description</Label>
                                <Textarea
                                    value={selectedPerception.customer_description}
                                    onChange={(e) =>
                                        setSelectedPerception({
                                            ...selectedPerception,
                                            customer_description: e.target.value
                                        })
                                    }
                                    rows={3}
                                />
                            </div>
                            <div>
                                <Label>Common Phrases (comma-separated)</Label>
                                <Textarea
                                    value={selectedPerception.common_phrases?.join(', ')}
                                    onChange={(e) =>
                                        setSelectedPerception({
                                            ...selectedPerception,
                                            common_phrases: e.target.value.split(',').map(s => s.trim())
                                        })
                                    }
                                    rows={3}
                                />
                            </div>
                            <div>
                                <Label>Technical Features (comma-separated)</Label>
                                <Textarea
                                    value={selectedPerception.technical_features?.join(', ')}
                                    onChange={(e) =>
                                        setSelectedPerception({
                                            ...selectedPerception,
                                            technical_features: e.target.value.split(',').map(s => s.trim())
                                        })
                                    }
                                    rows={2}
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSaveEdit}>Save Changes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
